import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  syncService,
  type SyncConflict,
  type SyncConflictDetail,
  type PushResult,
  type PullRecord,
} from '@/services/sync.service';
import {
  onQueueChange,
  onSyncActivity,
  getPendingCount,
  getConflictCount,
  getAttentionCount,
  retryDeadEntries,
} from '@/services/sync-queue.service';

// ─── Constants ──────────────────────────────────────────────────────────────

const LAST_SYNCED_KEY = 'bbpos_last_synced_at';

// ─── State ──────────────────────────────────────────────────────────────────

interface SyncState {
  lastSyncedAt: string | null;
  syncStatus: 'idle' | 'pushing' | 'pulling' | 'error';
  pendingCount: number;
  conflictCount: number;
  /** T19: this till's bills that failed or need review — the status-bar badge. */
  attentionCount: number;
  // G2.18: server-side pending conflict total, updated by refreshConflictCount
  // and by the realtime `sync:conflict` listener. Distinct from `conflictCount`
  // (which counts LOCAL queue conflicts derived from IndexedDB).
  pendingConflictCount: number;
  lastPushResult: PushResult | null;
  lastPullRecords: PullRecord[];
  conflicts: SyncConflict[];
  conflictsMeta: { page: number; limit: number; total: number; totalPages: number };
  /** Resolution filter of the last list fetch ('' = all), kept for refreshes. */
  conflictsFilter: string;
  selectedConflict: SyncConflictDetail | null;
  isLoadingConflict: boolean;
  isResolvingConflict: boolean;
  error: string | null;

  pushOfflineTransactions: () => Promise<void>;
  pullUpdates: (entityTypes?: string) => Promise<void>;
  refreshPendingCount: () => Promise<void>;
  fetchConflicts: (params?: { resolution?: string; page?: number; limit?: number }) => Promise<void>;
  fetchConflictDetail: (id: string) => Promise<void>;
  resolveConflict: (id: string, resolution: string, note?: string, mergedVersion?: Record<string, unknown>) => Promise<void>;
  /** G2.18: lightweight fetch of just the PENDING conflict total. Used by the
   *  realtime `sync:conflict` hook and by CASHIER who can't call /sync/conflicts. */
  refreshConflictCount: () => Promise<void>;
  clearSelectedConflict: () => void;
  clearError: () => void;
  /** PAGES.md §2.11: delete all resolved (non-PENDING) conflicts. */
  clearResolvedConflicts: () => Promise<void>;
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useSyncStore = create<SyncState>()(devtools((set, get) => {
  // Subscribe to queue changes to keep pendingCount and conflictCount in sync
  onQueueChange((count, conflicts, attention) => {
    set({ pendingCount: count, conflictCount: conflicts, attentionCount: attention });
  });

  // Load initial counts
  Promise.all([getPendingCount(), getConflictCount(), getAttentionCount()]).then(([count, conflicts, attention]) => {
    set({ pendingCount: count, conflictCount: conflicts, attentionCount: attention });
  });

  // Reflect every push run — the automatic one on reconnect too — in the
  // status, and advance "last synced" when it leaves the queue clean (the same
  // rule as pushOfflineTransactions).
  onSyncActivity((active, clean) => {
    if (active) {
      set({ syncStatus: 'pushing', error: null });
      return;
    }
    if (clean) {
      const now = new Date().toISOString();
      localStorage.setItem(LAST_SYNCED_KEY, now);
      set({ syncStatus: 'idle', lastSyncedAt: now });
    } else {
      set({ syncStatus: 'idle' });
    }
  });

  return {
  lastSyncedAt: localStorage.getItem(LAST_SYNCED_KEY),
  syncStatus: 'idle',
  pendingCount: 0,
  conflictCount: 0,
  attentionCount: 0,
  pendingConflictCount: 0,
  lastPushResult: null,
  lastPullRecords: [],
  conflicts: [],
  conflictsMeta: { page: 1, limit: 20, total: 0, totalPages: 0 },
  conflictsFilter: '',
  selectedConflict: null,
  isLoadingConflict: false,
  isResolvingConflict: false,
  error: null,

  pushOfflineTransactions: async () => {
    const pending = await getPendingCount();
    if (pending === 0) return;
    set({ syncStatus: 'pushing', error: null });
    try {
      // T17: 'Sync now' also gives DEAD rows (out of retries) another full try.
      await retryDeadEntries();
      const newPending = await getPendingCount();
      const newConflicts = await getConflictCount();

      // Only advance "last synced" if the queue is fully clean
      if (newPending === 0 && newConflicts === 0) {
        const now = new Date().toISOString();
        localStorage.setItem(LAST_SYNCED_KEY, now);
        set({ syncStatus: 'idle', lastSyncedAt: now, pendingCount: newPending, conflictCount: newConflicts });
      } else {
        set({ syncStatus: 'idle', pendingCount: newPending, conflictCount: newConflicts });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Push failed';
      set({ syncStatus: 'error', error: message });
    }
  },

  pullUpdates: async (entityTypes) => {
    set({ syncStatus: 'pulling', error: null });
    try {
      const params: Record<string, string | number> = {
        since: get().lastSyncedAt ?? '1970-01-01T00:00:00.000Z',
        limit: 100,
      };
      if (entityTypes) params.entity_type = entityTypes;

      const result = await syncService.pull(params);
      const now = new Date().toISOString();
      localStorage.setItem(LAST_SYNCED_KEY, now);
      set({
        syncStatus: 'idle',
        lastSyncedAt: now,
        lastPullRecords: result.data,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Pull failed';
      set({ syncStatus: 'error', error: message });
    }
  },

  refreshPendingCount: async () => {
    const count = await getPendingCount();
    set({ pendingCount: count });
  },

  fetchConflicts: async (params) => {
    try {
      const result = await syncService.listConflicts(params);
      set({
        conflicts: result.data,
        conflictsMeta: result.meta,
        conflictsFilter: params?.resolution ?? '',
        // Only a PENDING-filtered list counts pending conflicts: no filter
        // lists every resolution (the "All" option).
        pendingConflictCount:
          params?.resolution === 'PENDING' ? result.meta.total : get().pendingConflictCount,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load conflicts';
      set({ error: message });
    }
  },

  // GAP-4: CASHIER can view the pending conflict count but not the list/detail.
  // Use the dedicated lightweight endpoint at GET /sync/conflicts/count instead
  // of /sync/conflicts?limit=1 so CASHIER requests don't 403. Also used by the
  // realtime `sync:conflict` hook for MANAGER+.
  refreshConflictCount: async () => {
    try {
      const result = await syncService.getPendingConflictCount();
      set({ pendingConflictCount: result.pending_count });
    } catch (err) {
      // Non-fatal — keep the prior count rather than wiping it on transient
      // network failure. Logged but not surfaced to the user.
      const message = err instanceof Error ? err.message : 'Failed to refresh conflict count';
      console.warn('[sync-store] refreshConflictCount failed:', message);
    }
  },

  fetchConflictDetail: async (id) => {
    set({ selectedConflict: null, isLoadingConflict: true });
    try {
      const detail = await syncService.getConflict(id);
      set({ selectedConflict: detail, isLoadingConflict: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load conflict';
      set({ isLoadingConflict: false, error: message });
    }
  },

  resolveConflict: async (id, resolution, note, mergedVersion) => {
    set({ isResolvingConflict: true });
    try {
      await syncService.resolveConflict(id, {
        resolution,
        note,
        ...(mergedVersion ? { merged_version: mergedVersion } : {}),
      });
      set({ isResolvingConflict: false, selectedConflict: null });
      // Refresh the list the user is looking at (same filter), and the count.
      const { conflictsMeta, conflictsFilter } = get();
      await get().fetchConflicts({
        resolution: conflictsFilter || undefined,
        page: conflictsMeta.page,
        limit: conflictsMeta.limit,
      });
      await get().refreshConflictCount();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resolve conflict';
      set({ isResolvingConflict: false, error: message });
    }
  },

  clearSelectedConflict: () => set({ selectedConflict: null }),

  clearError: () => set({ error: null }),

  clearResolvedConflicts: async () => {
    try {
      await syncService.clearResolvedConflicts();
      // Refresh the conflict list after clearing
      const { conflictsMeta } = get();
      await get().fetchConflicts({ page: 1, limit: conflictsMeta.limit });
      await get().refreshConflictCount();
    } catch (err: unknown) {
      set({ error: (err as Error).message || 'Failed to clear resolved conflicts' });
    }
  },
  };
}, { name: 'sync-store' }));
